import React, { useState, useRef, useEffect } from 'react';

const OptimizedImage = ({ 
  src, 
  alt, 
  className = '', 
  style = {},
  priority = false,
  sizes = '100vw',
  quality = 80,
  fallback = null,
  loading = 'lazy',
  ...props 
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [inView, setInView] = useState(priority);
  const imgRef = useRef();

  // Generate WebP and AVIF sources from original PNG/JPG
  const generateModernSources = (originalSrc) => {
    if (!originalSrc || typeof originalSrc !== 'string') {
      return {
        original: originalSrc || '',
        extension: 'png'
      };
    }
    
    const extension = originalSrc.match(/\.(png|jpg|jpeg)$/i)?.[1] || 'png';
    
    return {
      original: originalSrc,
      extension
    };
  };

  // Intersection Observer for lazy loading
  useEffect(() => {
    if (!src || priority) {
      setInView(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1, rootMargin: '50px' }
    );

    if (imgRef.current) {
      observer.observe(imgRef.current);
    }

    return () => observer.disconnect();
  }, [priority, src]);

  // Preload critical images
  useEffect(() => {
    if (priority && src) {
      const link = document.createElement('link');
      link.rel = 'preload';
      link.as = 'image';
      link.href = src;
      document.head.appendChild(link);
    }
  }, [priority, src]);

  // Early return if no src provided (after all hooks)
  if (!src) {
    return fallback ? (
      <img src={fallback} alt={alt || ''} className={className} style={style} {...props} />
    ) : null;
  }

  const sources = generateModernSources(src);

  const handleLoad = () => {
    setIsLoaded(true);
  };

  const handleError = () => {
    setHasError(true);
  };

  // Generate responsive sizes
  const generateSizes = () => {
    if (typeof sizes === 'string') return sizes;
    return '(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw';
  };

  // Show placeholder only for non-priority images that aren't in view yet
  if (!inView && !priority) {
    return (
      <div 
        ref={imgRef} 
        className={`${className} img-placeholder`}
        style={{
          ...style,
          backgroundColor: '#f0f0f0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '200px'
        }}
        {...props}
      >
        {/* Placeholder content */}
      </div>
    );
  }

  // If there's an error and a fallback, use fallback
  if (hasError && fallback) {
    return <img src={fallback} alt={alt} className={className} style={style} {...props} />;
  }

  // Always render the image if we have a src and it's in view
  return (
    <img
      ref={imgRef}
      src={sources.original}
      alt={alt}
      className={`${className} ${isLoaded ? 'loaded' : 'loading'}`}
      style={{
        ...style,
        transition: 'opacity 0.3s ease',
        opacity: isLoaded ? 1 : 0.8,
      }}
      loading={priority ? 'eager' : loading}
      decoding={priority ? 'sync' : 'async'}
      onLoad={handleLoad}
      onError={handleError}
      {...props}
    />
  );
};

export default OptimizedImage;
